import $ from 'jquery';

/**
 * Scrollable Animations Widget JS Handler Class.
 */
class ScrollableAnimationsWidgetHandler extends elementorModules.frontend.handlers.SwiperBase {
	/**
	 * Get Default Settings
	 *
	 * @return {Object} An object of default settings.
	 */
	getDefaultSettings() {
		return {
			selectors: {
				canvas: '.scrollable-animations__canvas',
				component: '.scrollable-animations',
				bottom: '.scrollable-animations__bottom',
				intro: '.scrollable-animations__intro',
				introWrapper: '.scrollable-animations__intro-wrapper',
				pagination: '.scrollable-animations__pagination',
				paginationItem: '.scrollable-animations__page',
				pauseButton: '.scrollable-animations__controls',
				videoWrapper: '.scrollable-animations__video-wrapper',
			},
		};
	}

	/**
	 * Get Default Elements
	 *
	 * @return {Object} An object of jquery elements.
	 */
	getDefaultElements() {
		const selectors = this.getSettings('selectors');

		return {
			$canvas: this.$element.find(selectors.canvas),
			$component: this.$element.find(selectors.component),
			$bottom: this.$element.find(selectors.bottom),
			$intro: this.$element.find(selectors.intro),
			$introWrapper: this.$element.find(selectors.introWrapper),
			$pagination: this.$element.find(selectors.pagination),
			$paginationItem: this.$element.find(selectors.paginationItem),
			$pauseButton: this.$element.find(selectors.pauseButton),
			$videoWrapper: this.$element.find(selectors.videoWrapper),
		};
	}

	/**
	 * On Init
	 *
	 * Runs when the widget is loaded and initialized in the frontend.
	 */
	async onInit(...args) {
		super.onInit(...args);

		const type = this.getElementSettings('type');

		switch (type) {
			case 'frames':
				await this.onInitFrames();
				break;

			case 'video':
				this.onInitVideo();
				break;
		}
	}

	/**
	 * On Init Video
	 *
	 * Runs when the widget is loaded and initialized in the frontend.
	 */
	onInitVideo() {
		const $video = this.elements.$videoWrapper.find('video');

		if (!$video.length) {
			return;
		}

		const video = $video[0];
		const $component = this.elements.$component;
		const $pauseButton = this.elements.$pauseButton;
		const classes = {
			paused: 'js-video-is-paused',
			playing: 'js-video-is-playing',
		};

		const toggleVideo = (state) => {
			switch (state) {
				case 'play':
					video.play();
					$component.removeClass('is-finished');
					$pauseButton.removeClass(classes.paused);
					$pauseButton.addClass(classes.playing);
					break;

				case 'pause':
					video.pause();
					$pauseButton.removeClass(classes.playing);
					$pauseButton.addClass(classes.paused);
					break;
			}
		};

		$pauseButton.on('click', () => {
			if ($pauseButton.hasClass(classes.playing)) {
				toggleVideo('pause');
			} else {
				toggleVideo('play');
			}
		});

		$video.on('ended', () => {
			toggleVideo('pause');
			$component.addClass('is-finished');
		});

		// Trigger initial animation if container is in viewport.
		this.intersectionObserver = elementorModules.utils.Scroll.scrollObserver({
			callback: (event) => {
				if (event.isInViewport) {
					this.intersectionObserver.unobserve(this.elements.$videoWrapper[0]);
					toggleVideo('play');
				}
			},
		});

		this.intersectionObserver.observe(this.elements.$videoWrapper[0]);
	}

	/**
	 * On Init Frames
	 *
	 * Runs when the widget is loaded and initialized in the frontend.
	 */
	async onInitFrames() {
		const Swiper = elementorFrontend.utils.swiper;
		const frameSettings = this.getElementSettings('frames') || [];
		const frameCount = frameSettings.length;
		const html = document.documentElement;
		const canvas = this.elements.$canvas[0];
		const context = canvas.getContext('2d');
		const $component = this.elements.$component;
		const $bottom = this.elements.$bottom;
		const $intro = this.elements.$intro;
		const $introWrapper = this.elements.$introWrapper;
		const $pagination = this.elements.$pagination;
		const $paginationItem = this.elements.$paginationItem;
		const $videoWrapper = this.elements.$videoWrapper;
		let currentImageIndex = 0;
		let currentPageIndex = 0;

		// Initialize the video pagination.
		this.swiperControl = await new Swiper($pagination, {
			slidesPerView: 1.5,
			spaceBetween: 36,
			centeredSlides: true,
			centeredSlidesBounds: true,
			observer: true,
			observeParents: true,
			breakpoints: {
				700: {
					slidesPerView: 3,
				},
				1200: {
					slidesPerView: 5,
				},
			},
		});

		// Expose the swiper instance in the frontend
		$pagination.data('swiper', this.swiperControl);

		/**
		 * Draw Image Prop
		 *
		 * Used to replicate object-fit: cover.
		 *
		 * @param {Object} ctx The context.
		 * @param {Object} img The image.
		 * @param {Number} x The x axis.
		 * @param {Number} y The y axis.
		 * @param {Number} w The width.
		 * @param {Number} h The height.
		 * @param {Number} offsetX The x axis offset.
		 * @param {Number} offsetY The y axis offset.
		 *
		 * Credit:  Ken Fyrstenberg Nilsen
		 * https://stackoverflow.com/questions/21961839/simulation-background-size-cover-in-canvas/21961894#21961894
		 *
		 * If image and context are only arguments rectangle will equal canvas.
		 */
		const drawImageProp = (ctx, img, x, y, w, h, offsetX, offsetY) => {
			if (arguments.length === 2) {
				x = y = 0;
				w = ctx.canvas.width;
				h = ctx.canvas.height;
			}

			// default offset is center
			offsetX = typeof offsetX === 'number' ? offsetX : 0.5;
			offsetY = typeof offsetY === 'number' ? offsetY : 0.5;

			// keep bounds [0.0, 1.0]
			if (offsetX < 0) {
				offsetX = 0;
			}
			if (offsetY < 0) {
				offsetY = 0;
			}
			if (offsetX > 1) {
				offsetX = 1;
			}
			if (offsetY > 1) {
				offsetY = 1;
			}

			let iw = img.width,
				ih = img.height,
				r = Math.min(w / iw, h / ih),
				nw = iw * r, // new prop. width
				nh = ih * r, // new prop. height
				cx,
				cy,
				cw,
				ch,
				ar = 1;

			// decide which gap to fill
			if (nw < w) {
				ar = w / nw;
			}
			if (Math.abs(ar - 1) < 1e-14 && nh < h) {
				ar = h / nh; // updated
			}
			nw *= ar;
			nh *= ar;

			// calc source rectangle
			cw = iw / (nw / w);
			ch = ih / (nh / h);

			cx = (iw - cw) * offsetX;
			cy = (ih - ch) * offsetY;

			// make sure source rectangle is valid
			if (cx < 0) {
				cx = 0;
			}
			if (cy < 0) {
				cy = 0;
			}
			if (cw > iw) {
				cw = iw;
			}
			if (ch > ih) {
				ch = ih;
			}

			// fill image in dest. rectangle
			ctx.drawImage(img, cx, cy, cw, ch, x, y, w, h);
		};

		// Get canvas sizes.
		const getCanvasSizes = () => {
			return {
				height: window.innerHeight,
				width: window.innerWidth,
			};
		};

		// Set canvas height/width.
		const setCanvasSize = () => {
			const sizes = getCanvasSizes();
			canvas.height = sizes.height;
			canvas.width = sizes.width;
			$intro.height(sizes.height);
		};

		// Stop any running videos.
		const stopVideo = (index) => {
			if (typeof index === 'undefined') {
				index = currentPageIndex;
			}
			const $currentVideo = $videoWrapper.eq(index).find('video');
			if ($currentVideo.length) {
				const video = $currentVideo[0];
				if (!!(video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2)) {
					video.pause();
				}
			}
		};

		// Get current image url.
		const currentFrame = (index) => {
			if (!frameSettings[index]) {
				return '';
			}
			return frameSettings[index].url || '';
		};

		// Preload images.
		const preloadImages = () => {
			for (let i = 0; i < frameCount; i++) {
				const img = new Image();
				img.src = currentFrame(i);
			}
		};

		// Create an image on the canvas.
		const createImage = (index) => {
			const img = new Image();
			img.src = currentFrame(index);
			img.onload = function () {
				const sizes = getCanvasSizes();
				drawImageProp(context, img, 0, 0, sizes.width, sizes.height);
			};
		};

		// Update the image on the canvas.
		const updateImage = (index, force) => {
			if (currentImageIndex === index && !force) {
				return;
			}
			currentImageIndex = index;
			createImage(index);
		};

		function resizeCanvas() {
			setCanvasSize();
			updateImage(currentImageIndex, true);
		}

		// Set canvas size.
		setCanvasSize();
		$(window).on('resize', resizeCanvas);

		// Preload images and set up initial canvas frame.
		preloadImages();
		createImage(0);

		// Frame through the video on scroll.
		const topOffsetPadding = 500;
		const bottomOffsetPadding = window.innerHeight * 2;

		$(window).on('scroll', () => {
			const elementHeight = this.elements.$component.height();
			const componentOffsetTop = this.elements.$component.offset().top;
			const componentOffsetBottom = componentOffsetTop + elementHeight - bottomOffsetPadding;
			const scrollTop = html.scrollTop;
			const offsetScrollTop = scrollTop - componentOffsetTop - topOffsetPadding;
			const maxScrollTop = componentOffsetBottom - componentOffsetTop - window.innerHeight * 1.5;
			const scrollFraction = offsetScrollTop / maxScrollTop;
			// Adjust padding for more scrolling.
			let frameCountPadding = 0;
			if (window.innerWidth <= 768) {
				frameCountPadding = 20;
			}
			const frameIndex = Math.min(
				frameCount + frameCountPadding - 1,
				Math.ceil(scrollFraction * (frameCount + frameCountPadding))
			);

			// Removed fixed when scrolling past.
			// This needs to be above the next condition so the class can
			// be added back in if scrolling up.
			// Note: We add back in the bottom offset padding to reset the
			// initial value and then subtract the window height. This will get
			// the item to complete when we scroll to the very bottom.
			if (scrollTop > componentOffsetBottom + bottomOffsetPadding - window.innerHeight) {
				$component.addClass('is-complete');
				$component.removeClass('is-fixed');
			} else {
				$component.removeClass('is-complete');
			}

			// Make fixed when scrolled to.
			if (scrollTop > componentOffsetTop) {
				$component.addClass('is-fixed');
				// Animate intro text.
				const opacity = 1 - (frameIndex * 3) / 100;

				// Pause for topOffsetPadding amount before animating text.
				if (scrollTop > componentOffsetTop + topOffsetPadding) {
					if (opacity >= 0) {
						$intro.css({ display: 'flex', 'z-index': 15 });
						$introWrapper.css({
							transform: 'scale(' + (frameIndex * 3 + 100) / 100 + ')',
							opacity: opacity,
						});
					} else {
						$intro.css({ display: 'none' });
					}
				}
			} else {
				// Reset state back to original.
				$component.removeClass('is-fixed');
				$intro.css({ display: 'flex', 'z-index': 15 });
				$introWrapper.css({
					transform: 'scale(1)',
					opacity: 1,
				});
			}

			// Update current frame.
			if (frameIndex >= 0 && frameIndex < frameCount - 1 && !$bottom.hasClass('is-active')) {
				// Stop any playing videos.
				stopVideo();
				// Update frame.
				requestAnimationFrame(() => updateImage(frameIndex + 1));
			}

			// Fade in/out the text pagination and videos.
			// Add 20 to bring in the pagination before animation is completely
			// finished.
			if (frameIndex + 20 >= frameCount) {
				$bottom.addClass('is-active');
			} else {
				$bottom.removeClass('is-active');
				// Hide any videos that may be playing.
				$videoWrapper.removeClass('is-active');
				// Reset pagination if not on the first page already.
				const $firstPage = $paginationItem.eq(0);
				if (!$firstPage.hasClass('is-active')) {
					// Timeout to wait until bottom has faded out completely.
					setTimeout(() => {
						$paginationItem.removeClass('is-active');
						$firstPage.addClass('is-active');
						this.swiperControl.slideTo(0);
					}, 300);
				}
			}
		});

		// Pagination events.
		$paginationItem.on('click', (e) => {
			// Fill canvas in all black to get rid of the last frame.
			context.fillStyle = '#000';
			context.fillRect(0, 0, canvas.width, canvas.height);

			const index = $paginationItem.index($(e.currentTarget));
			const $currentVideoWrapper = $videoWrapper.eq(index);
			const $video = $currentVideoWrapper.find('video');

			// Toggle paginationItem classes.
			$paginationItem.removeClass('is-active');
			$paginationItem.eq(index).addClass('is-active');

			// Update video wrapper positioning.
			const bottomHeight = $bottom.outerHeight();
			$videoWrapper.css({
				height: 'calc(100vh - ' + bottomHeight + 'px)',
			});

			// Stop any non-active playing videos. Do this after a slight delay
			// for a more graceful fade out effect.
			if (currentPageIndex !== index) {
				const origCurrentPageIndex = currentPageIndex;
				setTimeout(() => {
					stopVideo(origCurrentPageIndex);
				}, 1000);
			}

			// Set new index and start video.
			currentPageIndex = index;
			this.swiperControl.slideTo(index);

			$videoWrapper.removeClass('is-active');

			if ($video.length) {
				$currentVideoWrapper.addClass('is-active');
				// Reset video back to beginning and replay.
				$video[0].pause();
				$video[0].load();
				$video[0].play();
			} else {
				updateImage(frameCount - 1, true);
			}
		});
	}
}

/**
 * Register JS Handler for the Scrollable Animations Widget
 */
elementorFrontend.elements.$window.on('elementor/frontend/init', () => {
	elementorFrontend.elementsHandler.attachHandler('scrollable-animations', ScrollableAnimationsWidgetHandler);
});
